import {
  forwardRef,
  useId,
  type InputHTMLAttributes,
  type ReactNode,
} from 'react';
import { cn } from '@/lib/cn';

/* --------------------------------------------------------------------------
   Types
   -------------------------------------------------------------------------- */

export interface InputProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, 'size'> {
  /** Visible label text. */
  label?: string;
  /** Helper text shown below the input. */
  helperText?: string;
  /** Error message — overrides helperText when present. */
  error?: string;
  /** Icon element rendered at the start of the input. */
  icon?: ReactNode;
  /** Full width of parent (default: true). */
  fullWidth?: boolean;
}

/* --------------------------------------------------------------------------
   Component
   -------------------------------------------------------------------------- */

export const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      className,
      label,
      helperText,
      error,
      icon,
      fullWidth = true,
      id: externalId,
      disabled,
      ...props
    },
    ref,
  ) => {
    const generatedId = useId();
    const id = externalId ?? generatedId;
    const hasError = Boolean(error);
    const hintId = `${id}-hint`;

    return (
      <div className={cn('flex flex-col gap-1.5', fullWidth && 'w-full')}>
        {/* Label */}
        {label && (
          <label
            htmlFor={id}
            className={cn(
              'font-[family-name:var(--font-heading)] text-sm font-semibold',
              'text-text-secondary',
              hasError && 'text-status-negative',
            )}
          >
            {label}
          </label>
        )}

        {/* Input wrapper */}
        <div className="relative">
          {/* Icon prefix */}
          {icon && (
            <span
              className={cn(
                'pointer-events-none absolute left-4 top-1/2 -translate-y-1/2',
                'text-text-muted [&>svg]:h-5 [&>svg]:w-5',
                hasError && 'text-status-negative',
              )}
              aria-hidden
            >
              {icon}
            </span>
          )}

          <input
            ref={ref}
            id={id}
            disabled={disabled}
            aria-invalid={hasError || undefined}
            aria-describedby={helperText || error ? hintId : undefined}
            className={cn(
              /* Base */
              'h-[var(--touch-min)] w-full rounded-[var(--radius-md)]',
              'bg-surface-card border border-border-default',
              'px-4 text-base text-text-primary',
              'font-[family-name:var(--font-body)]',
              'placeholder:text-text-muted',
              /* Transitions */
              'transition-all duration-[var(--duration-normal)] ease-[var(--ease-out)]',
              /* Focus */
              'focus:border-border-focus focus:ring-2 focus:ring-accent-primary-light',
              'focus:outline-none',
              /* Hover (non-focused) */
              'hover:border-border-hover',
              /* Disabled */
              'disabled:bg-surface-sunken disabled:text-text-muted disabled:cursor-not-allowed',
              /* Error */
              hasError && 'border-border-error focus:border-border-error focus:ring-status-negative-light',
              /* Icon padding */
              icon && 'pl-12',
              className,
            )}
            {...props}
          />
        </div>

        {/* Helper / Error text */}
        {(error || helperText) && (
          <p
            id={hintId}
            className={cn(
              'text-sm',
              hasError ? 'text-status-negative' : 'text-text-muted',
            )}
          >
            {error || helperText}
          </p>
        )}
      </div>
    );
  },
);

Input.displayName = 'Input';
